import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { classifyListSearch, filteredHead, paginatedHead, trackingParams } from "@/utils/listSearchParams";
import Vehicles from "@/pages/Vehicles";
import { routeHead } from "@/lib/seo/route-head";
import { paginatedListingsQueryOptions } from "@/hooks/usePaginatedListings";
import { FILTER_ALIASES, readSavedSearchFields } from '@/utils/savedSearchFilters';
import { VEHICLE_SORTS, resolveVehicleSort } from '@/utils/vehicleSort';

export const Route = createFileRoute("/vehicles/")({
  head: ({ match }) => {
    const base = routeHead("/vehicles/");
    const kind = classifyListSearch((match?.search ?? {}) as Record<string, unknown>);
    if (kind.kind === "page") return paginatedHead(base, "/vehicles", kind.page);
    if (kind.kind === "filtered") return filteredHead(base);
    return base;
  },
  validateSearch: (search: Record<string, unknown>) =>
    Object.fromEntries(
      Object.entries(search).map(([k, v]) => [k, typeof v === "number" ? String(v) : v]).filter((entry): entry is [string, string] => typeof entry[1] === "string"),
    ),
  loaderDeps: ({ search }) => Object.fromEntries(
    ['sort', 'type', 'make', 'marca', 'brand', 'model', 'priceFrom', 'priceMin', 'priceTo', 'priceMax', 'yearFrom', 'yearMin', 'yearTo', 'yearMax', 'mileageFrom', 'mileageTo', 'mileageMax', 'fuelType', 'fuel', 'transmission', 'condition', 'location', 'city', 'seats', 'page', 'dealer', ...Object.values(FILTER_ALIASES).flat()].map(key => [key, search[key]]),
  ),
  loader: async ({ context, location }) => {
    // ?page=1 → clean URL (301); invalid page value → 404. Server-only.
    if (typeof window === "undefined") {
      const kind = classifyListSearch(location.search as Record<string, unknown>);
      if (kind.kind === "invalid-page") throw notFound();
      if (kind.kind === "page-one") {
        const t = trackingParams(location.search as Record<string, unknown>).toString();
        throw redirect({ href: `/vehicles${t ? `?${t}` : ""}`, statusCode: 301 });
      }
    }
    const search = new URLSearchParams(location.searchStr);
    const sortQuery = VEHICLE_SORTS[resolveVehicleSort(search.get('sort'))];
    const categoryByType = {
      car: "cars",
      truck: "heavy_truck",
      motorcycle: "motorcycle",
      van: "van",
      trailer: "trailer",
      agricultural: "agricultural",
      construction: "construction",
      boat: "boat",
      atv: "atv",
    } as const;
    const vehicleType = search.get("type") as keyof typeof categoryByType | null;
    const numberParam = (name: string) => {
      const value = search.get(name);
      if (!value) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const alias = readSavedSearchFields(search);
    const num = (v?: string) => { if (!v) return null; const x = Number(v); return Number.isFinite(x) ? x : null; };
    const prefetch = context.queryClient.ensureInfiniteQueryData(
      paginatedListingsQueryOptions({
        country_code: "RO",
        category_code: vehicleType ? categoryByType[vehicleType] : null,
        make: search.get("make") || alias.marca || null,
        model: search.get("model"),
        price_min: numberParam("priceFrom") ?? numberParam("priceMin") ?? num(alias.pretMin),
        price_max: numberParam("priceTo") ?? numberParam("priceMax") ?? num(alias.pretMax),
        year_min: numberParam("yearFrom") ?? numberParam("yearMin") ?? num(alias.anMin),
        year_max: numberParam("yearTo") ?? numberParam("yearMax") ?? num(alias.anMax),
        mileage_min: numberParam("mileageFrom"),
        mileage_max: numberParam("mileageTo") ?? numberParam("mileageMax") ?? num(alias.kmMax),
        fuel: search.get("fuelType") || alias.combustibil || null,
        transmission: alias.transmisie || null,
        condition: search.get("condition"),
        location: search.get("location") || alias.locatie || null,
        seats: numberParam("seats"),
        sort_by: sortQuery.sort_by,
        sort_order: sortQuery.sort_order,
        pageSize: 10,
        page: numberParam("page") ?? 1,
        user_id: search.get("dealer"),
      }),
    );
    // Pe server așteptăm datele (SEO). În browser nu blocăm schimbarea paginii:
    // lista veche rămâne afișată până sosesc rezultatele noi, fără flash.
    if (typeof window === "undefined") {
      const data = (await prefetch.catch(() => undefined)) as { pages?: Array<{ listings?: unknown[] }> } | undefined;
      // A page past the last one is a real 404 (page 1 with no stock stays 200 + noindex).
      const pageNum = numberParam("page") ?? 1;
      if (pageNum > 1 && data?.pages?.[0] && (data.pages[0].listings?.length ?? 0) === 0) throw notFound();
    }
    else void prefetch.catch(() => {});
  },
  errorComponent: function VehiclesError({ error }: { error: unknown }) {
    return (
      <main className="container mx-auto max-w-7xl px-4 py-12">
        <h1 className="text-3xl font-bold">Vehiculele nu pot fi încărcate momentan</h1>
        <p className="mt-3 text-muted-foreground" role="alert">{error instanceof Error ? error.message : String(error)}</p>
      </main>
    );
  },
  notFoundComponent: () => (
    <main className="container mx-auto max-w-7xl px-4 py-12">
      <h1 className="text-3xl font-bold">Pagina nu a fost găsită</h1>
      <a href="/vehicles" className="mt-6 inline-block rounded-lg bg-primary px-5 py-3 font-medium text-primary-foreground">Vezi toate vehiculele</a>
    </main>
  ),
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <div className="surface-light min-h-screen"><Vehicles /></div>
  );
}
